import React from "react";

import StickyModalFooter from "@components/General/StickyModalFooter";

import type { ColaboratorInfo } from "@models/Colaborators";
import type { QontactPointInformation, ShiftRecipeWithUserIDs } from "@models/QontactPoint";
import { GhostButton, HollowedButton, NormalButton } from "@shared/designSystem/Button";
import { HorizontalContainer, SimpleCardContainer, VerticalContainer } from "@shared/designSystem/Containers";
import { Modal } from "@shared/designSystem/messages/Modal";
import { ProgressBar } from "@shared/designSystem/ProgressBar";
import SearchBar from "@shared/designSystem/SearchBar";
import { SelectionPill } from "@shared/designSystem/SelectionPill";
import StatusDot from "@shared/designSystem/StatusDot";
import { TextInput } from "@shared/designSystem/TextInput";
import { ErrorText, SmallText, Text } from "@shared/designSystem/Texts";
import designSystemStyles from "@shared/styles/designSystemStyles";
// Same pill look as the DS FilterPillGroup, but several days can be selected
import pillStyles from "@shared/styles/components/FilterPillGroup.module.css";
import { shiftWeekdays, useShiftRecipeModalViewModel } from "./ShiftRecipeModalViewModel";

const initialsOf = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    return (parts.length === 1 ? parts[0][0] : `${parts[0][0]}${parts[parts.length - 1][0]}`).toUpperCase();
};

/** Small (28px) avatar for the compact collaborator rows: photo or initials, like UserCard */
const CollaboratorAvatar: React.FC<{ name: string; imageURL?: string }> = ({ name, imageURL }) => (
    <div
        aria-hidden="true"
        style={{
            width: "28px",
            height: "28px",
            borderRadius: "50%",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `1px solid ${designSystemStyles.colorUserInitialsBorder}`,
            backgroundColor: designSystemStyles.colorUserInitialsBackground,
            backgroundImage: imageURL ? `url(${imageURL})` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
            fontSize: designSystemStyles.fontSizeXxsm,
            fontWeight: designSystemStyles.fontWeightTitle,
            color: designSystemStyles.colorTitleText,
        }}
    >
        {!imageURL && initialsOf(name)}
    </div>
);

/** Neutral 1px line between sections (the DS HorizontalDivider is orange) */
const SectionSeparator: React.FC = () => (
    <div aria-hidden="true" style={{ width: "100%", height: "1px", backgroundColor: designSystemStyles.colorTextInputBorder }} />
);

/** Multi-select day pill, same look as the DS FilterPillGroup pills */
const DayPill: React.FC<{ label: string; isSelected: boolean; disabled: boolean; onClick: () => void }> = ({
    label,
    isSelected,
    disabled,
    onClick,
}) => (
    <button
        type="button"
        aria-pressed={isSelected}
        disabled={disabled}
        onClick={onClick}
        className={[pillStyles.pill, isSelected ? pillStyles.selected : ""].filter(Boolean).join(" ")}
    >
        <Text
            text={isSelected ? `**${label}**` : label}
            markdown={isSelected}
            color={isSelected ? designSystemStyles.colorMainAction : designSystemStyles.colorText}
        />
    </button>
);

/** Above this many assigned people, the rest is summarized as "y N más" */
const maxVisiblePills = 8;

interface ShiftRecipeModalProps {
    orgID: string;
    qontactPoint: QontactPointInformation;
    /** undefined = "Agregar turno" */
    recipe?: ShiftRecipeWithUserIDs;
    collaborators: ColaboratorInfo[];
    onClose: () => void;
    onSaved: (recipe: ShiftRecipeWithUserIDs, isNew: boolean) => void;
}

/**
 * Add / edit shift modal (design 3c, "total + asignados"). Mounted only while
 * open, so every opening starts from the given recipe.
 */
const ShiftRecipeModal: React.FC<ShiftRecipeModalProps> = (props) => {
    const viewModel = useShiftRecipeModalViewModel(props);

    return (
        <Modal
            isOpen={true}
            onClose={viewModel.isSaving ? () => {} : props.onClose}
            title={viewModel.isNew ? "Agregar turno".localized() : "Editar turno".localized()}
            width="800px"
            maxWidth="94vw"
            maxHeight="92vh"
        >
            <VerticalContainer width="100%" gap={designSystemStyles.gapLg} margin="0">
                <TextInput
                    label={"Nombre del turno".localized()}
                    value={viewModel.name}
                    placeholder={"nombre del turno".localized()}
                    error={viewModel.errors.name}
                    onChange={viewModel.setName}
                    disabled={viewModel.isSaving}
                />

                <VerticalContainer width="100%" gap={designSystemStyles.gapXxxSm} margin="0">
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: designSystemStyles.gapMd, width: "100%" }}>
                        <TextInput
                            label={"Desde".localized()}
                            value={viewModel.hourFrom}
                            placeholder="00:00"
                            inputMode="numeric"
                            maxLength={5}
                            onChange={viewModel.setHourFrom}
                            disabled={viewModel.isSaving}
                        />
                        <TextInput
                            label={"Hasta".localized()}
                            value={viewModel.hourTo}
                            placeholder="00:00"
                            inputMode="numeric"
                            maxLength={5}
                            onChange={viewModel.setHourTo}
                            disabled={viewModel.isSaving}
                        />
                    </div>
                    {viewModel.errors.hours && <ErrorText text={viewModel.errors.hours} />}
                </VerticalContainer>

                <VerticalContainer width="100%" gap={designSystemStyles.gapXsm} margin="0">
                    <SmallText text={"Días".localized()} />
                    <div className={pillStyles.group} role="group" aria-label={"Días".localized()}>
                        <DayPill
                            label={"Todos los días".localized()}
                            isSelected={viewModel.isEveryDay}
                            disabled={viewModel.isSaving}
                            onClick={viewModel.toggleEveryDay}
                        />
                        {shiftWeekdays.map((day) => (
                            <DayPill
                                key={day.index}
                                label={day.label.localized()}
                                isSelected={viewModel.weekdays.includes(day.index)}
                                disabled={viewModel.isSaving}
                                onClick={() => viewModel.toggleWeekday(day.index)}
                            />
                        ))}
                    </div>
                    {viewModel.errors.days && <ErrorText text={viewModel.errors.days} />}
                </VerticalContainer>

                <SectionSeparator />

                <VerticalContainer width="100%" gap={designSystemStyles.gapSm} margin="0">
                    <Text text={`**${"¿Cuántas personas espera este turno?".localized()}**`} markdown={true} />
                    <SmallText
                        text={"Indicá el total. Después podés asignar a algunas o a todas por nombre.".localized()}
                        color={designSystemStyles.colorTextSecondary}
                    />
                    <SmallText text={"Personas esperadas".localized()} />
                    <HorizontalContainer width="auto" gap={designSystemStyles.gapSm} justifyContent="flex-start">
                        <HollowedButton
                            text="−"
                            onClick={viewModel.decrementExpected}
                            disabled={!viewModel.canDecrement || viewModel.isSaving}
                            width="44px"
                        />
                        <TextInput
                            label=""
                            value={viewModel.expectedInput}
                            inputMode="numeric"
                            onChange={viewModel.onExpectedTyped}
                            onLostFocus={viewModel.onExpectedBlur}
                            width="96px"
                            disabled={viewModel.isSaving}
                        />
                        <HollowedButton
                            text="+"
                            onClick={viewModel.incrementExpected}
                            disabled={viewModel.isSaving}
                            width="44px"
                        />
                    </HorizontalContainer>
                    {viewModel.isTypedBelowAssigned && (
                        <ErrorText
                            text={"Mínimo %s: hay %s personas asignadas. Quitá alguna para bajar el total.".localized(
                                String(viewModel.assignedCount),
                                String(viewModel.assignedCount)
                            )}
                        />
                    )}
                    {viewModel.errors.expected && <ErrorText text={viewModel.errors.expected} />}

                    <SimpleCardContainer variant="inlineSetup">
                    <VerticalContainer width="100%" gap={designSystemStyles.gapSm} margin="0">
                        <HorizontalContainer width="100%" gap={designSystemStyles.gapMd} justifyContent="flex-start" flexWrap="wrap">
                            <HorizontalContainer width="auto" gap={designSystemStyles.gapXsm}>
                                <StatusDot color={designSystemStyles.colorMainAction} />
                                <SmallText text={"**%s** asignadas por nombre".localized(String(viewModel.assignedCount))} markdown={true} />
                            </HorizontalContainer>
                            {viewModel.freeSlots > 0 && (
                                <HorizontalContainer width="auto" gap={designSystemStyles.gapXsm}>
                                    <StatusDot color={designSystemStyles.colorStatusVoided} />
                                    <SmallText
                                        text={(viewModel.freeSlots === 1
                                            ? "Y 1 colaborador más"
                                            : "Y %s colaboradores más").localized(String(viewModel.freeSlots))}
                                    />
                                </HorizontalContainer>
                            )}
                        </HorizontalContainer>
                        <ProgressBar progress={viewModel.progress} ariaLabel={"Lugares asignados por nombre".localized()} />
                        <SmallText text={viewModel.hint} color={designSystemStyles.colorTextSupporting} />
                    </VerticalContainer>
                    </SimpleCardContainer>
                </VerticalContainer>

                <SectionSeparator />

                <VerticalContainer width="100%" gap={designSystemStyles.gapSm} margin="0">
                    <Text text={`**${"Colaboradores asignados".localized()}**`} markdown={true} />
                    <SmallText
                        text={"Las personas asignadas deben fichar en este turno. Los lugares sin asignar los puede cubrir cualquier colaborador.".localized()}
                        color={designSystemStyles.colorTextSecondary}
                    />
                    {/* Assigned people right under the explanation, above the search */}
                    {viewModel.assignedCollaborators.length > 0 && (
                        <HorizontalContainer width="100%" gap={designSystemStyles.gapXsm} flexWrap="wrap" justifyContent="flex-start">
                            {viewModel.areAllCollaboratorsAssigned ? (
                                // Everyone assigned: a single pill (an org can have hundreds of people)
                                <SelectionPill
                                    label={"Todos los colaboradores (%s)".localized(String(viewModel.assignedCollaborators.length))}
                                    onRemove={viewModel.unassignAll}
                                    disabled={viewModel.isSaving}
                                    ariaLabel={"Quitar a todos los colaboradores".localized()}
                                />
                            ) : (
                                <>
                                    {viewModel.assignedCollaborators.slice(0, maxVisiblePills).map((collaborator) => (
                                        <SelectionPill
                                            key={collaborator.id}
                                            label={collaborator.name}
                                            onRemove={() => viewModel.toggleCollaborator(collaborator.id)}
                                            disabled={viewModel.isSaving}
                                        />
                                    ))}
                                    {viewModel.assignedCollaborators.length > maxVisiblePills && (
                                        <SmallText
                                            text={"y %s más (los ves tildados en la lista)".localized(
                                                String(viewModel.assignedCollaborators.length - maxVisiblePills)
                                            )}
                                            color={designSystemStyles.colorTextSecondary}
                                        />
                                    )}
                                </>
                            )}
                        </HorizontalContainer>
                    )}
                    <SearchBar
                        placeholder={"buscar colaborador".localized()}
                        value={viewModel.search}
                        onChange={viewModel.setSearch}
                    />
                    <div
                        style={{
                            maxHeight: "240px",
                            overflowY: "auto",
                            width: "100%",
                            border: `1px solid ${designSystemStyles.colorTextInputBorder}`,
                            borderRadius: designSystemStyles.cornerRadiusComponent,
                        }}
                    >
                        {viewModel.filteredCollaborators.length === 0 ? (
                            <div style={{ padding: designSystemStyles.paddingMd }}>
                                <SmallText text={"No hay colaboradores para mostrar.".localized()} color={designSystemStyles.colorTextSecondary} />
                            </div>
                        ) : (
                            <>
                            <div
                                role="checkbox"
                                aria-checked={viewModel.areAllVisibleAssigned}
                                tabIndex={0}
                                onClick={() => !viewModel.isSaving && viewModel.toggleAllVisible()}
                                onKeyDown={(event) => {
                                    if ((event.key === " " || event.key === "Enter") && !viewModel.isSaving) {
                                        event.preventDefault();
                                        viewModel.toggleAllVisible();
                                    }
                                }}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: designSystemStyles.gapSm,
                                    height: "44px",
                                    padding: `0 ${designSystemStyles.paddingSm}`,
                                    boxSizing: "border-box",
                                    cursor: viewModel.isSaving ? "default" : "pointer",
                                    borderBottom: `1px solid ${designSystemStyles.colorTextInputBorder}`,
                                    background: designSystemStyles.colorBackgroundGray3,
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={viewModel.areAllVisibleAssigned}
                                    readOnly
                                    tabIndex={-1}
                                    style={{ width: "16px", height: "16px", margin: 0, accentColor: "var(--color-main-action)", pointerEvents: "none" }}
                                />
                                <Text
                                    text={`**${"Seleccionar todos".localized()}** (${viewModel.filteredCollaborators.length})`}
                                    markdown={true}
                                />
                            </div>
                            {viewModel.filteredCollaborators.map((collaborator) => {
                                const isAssigned = viewModel.isAssigned(collaborator.id);
                                return (
                                    <div
                                        key={collaborator.id}
                                        role="checkbox"
                                        aria-checked={isAssigned}
                                        tabIndex={0}
                                        onClick={() => !viewModel.isSaving && viewModel.toggleCollaborator(collaborator.id)}
                                        onKeyDown={(event) => {
                                            if ((event.key === " " || event.key === "Enter") && !viewModel.isSaving) {
                                                event.preventDefault();
                                                viewModel.toggleCollaborator(collaborator.id);
                                            }
                                        }}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: designSystemStyles.gapSm,
                                            height: "44px",
                                            padding: `0 ${designSystemStyles.paddingSm}`,
                                            boxSizing: "border-box",
                                            cursor: viewModel.isSaving ? "default" : "pointer",
                                            borderBottom: `1px solid ${designSystemStyles.colorTextInputBorder}`,
                                            background: isAssigned ? designSystemStyles.colorTintedAction : undefined,
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isAssigned}
                                            readOnly
                                            tabIndex={-1}
                                            style={{ width: "16px", height: "16px", margin: 0, accentColor: "var(--color-main-action)", pointerEvents: "none" }}
                                        />
                                        <CollaboratorAvatar name={collaborator.name ?? ""} imageURL={collaborator.imageURL} />
                                        <Text text={collaborator.name ?? ""} truncate={true} />
                                    </div>
                                );
                            })}
                            </>
                        )}
                    </div>
                </VerticalContainer>

                {/* Fixed footer: stays visible while the content scrolls */}
                <StickyModalFooter>
                <HorizontalContainer width="100%" justifyContent="flex-end" gap={designSystemStyles.gapSm}>
                    <GhostButton text={"Cancelar".localized()} onClick={props.onClose} width="140px" disabled={viewModel.isSaving} />
                    <NormalButton
                        text={"Guardar turno".localized()}
                        onClick={viewModel.save}
                        width="180px"
                        loading={viewModel.isSaving}
                        disabled={viewModel.isSaving}
                    />
                </HorizontalContainer>
                </StickyModalFooter>
            </VerticalContainer>
        </Modal>
    );
};

export default ShiftRecipeModal;
